// Support log (CR_20260913 S3): FleetSpoke's visits to a customer account and what changed. RLS shows a super admin
// every account's log and a manager their own account's; users see none.
import type { SupportPlace } from '@fleetspoke/core/support';
import type { SupportRole } from '@fleetspoke/core/types';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { recordTarget } from '../lib/supportRecords';

export interface SupportSession {
  id: string;
  admin_name: string;
  company_id: string;
  reason: string;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  end_reason: 'exit' | 'expired' | 'signed_out' | 'replaced' | 'admin_removed' | null;
  /** Where the visit was opened (CR_20260924). */
  origin: 'web' | 'phone';
  changes: number;
  /** Every role the visit acted as, oldest first (CR_20260924 Part C); empty from a server without Part C. */
  roles: SupportRoleChange[];
}

export interface SupportRoleChange {
  act_as: SupportRole;
  act_scope: 'all' | 'selected';
  act_site_ids: string[] | null;
  act_project_ids: string[] | null;
  at: string;
}

export interface SupportChange {
  id: number;
  table_name: string;
  op: 'INSERT' | 'UPDATE' | 'DELETE';
  row_id: string | null;
  ref_table: string | null;
  ref_id: string | null;
  changed_columns: string[] | null;
  at: string;
}

async function rows<T>(
  q: PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<T> {
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data as T;
}

export function useSupportSessions(companyId: string) {
  return useQuery({
    queryKey: ['support', 'sessions', companyId],
    queryFn: async () => {
      const list = await rows<
        (Omit<SupportSession, 'changes' | 'roles'> & {
          support_audit: { count: number }[];
          support_role_changes?: SupportRoleChange[];
        })[]
      >(
        supabase()
          .from('support_sessions')
          .select(
            'id, admin_name, company_id, reason, started_at, expires_at, ended_at, end_reason, origin, support_audit(count), ' +
              'support_role_changes(act_as, act_scope, act_site_ids, act_project_ids, at)',
          )
          .eq('company_id', companyId)
          .order('started_at', { ascending: false })
          .limit(100),
      );
      return list.map(({ support_audit, support_role_changes, ...s }) => ({
        ...s,
        changes: support_audit[0]?.count ?? 0,
        roles: [...(support_role_changes ?? [])].sort((a, b) => a.at.localeCompare(b.at)),
      }));
    },
  });
}

export function useSupportChanges(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['support', 'changes', sessionId],
    enabled,
    queryFn: () =>
      rows<SupportChange[]>(
        supabase()
          .from('support_audit')
          .select('id, table_name, op, row_id, ref_table, ref_id, changed_columns, at')
          .eq('session_id', sessionId)
          .order('at')
          .limit(500),
      ),
  });
}

// The name each linked record goes by today, read live (the audit stores ids only). RLS applies, so a manager
// only resolves their own account's records.
type Row = Record<string, unknown>;
const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const LOOKUPS: Record<string, { key: string; columns: string; label: (r: Row) => string }> = {
  assets: {
    key: 'id',
    columns: 'id, asset_code, description',
    label: (r) => [text(r.asset_code), text(r.description)].filter(Boolean).join(' · '),
  },
  transfers: { key: 'id', columns: 'id, ref', label: (r) => text(r.ref) },
  asset_requests: { key: 'id', columns: 'id, ref', label: (r) => text(r.ref) },
  stock_counts: {
    key: 'id',
    columns: 'id, sites(name)',
    label: (r) => text((r.sites as Row | null)?.name),
  },
  sites: { key: 'id', columns: 'id, name', label: (r) => text(r.name) },
  projects: { key: 'id', columns: 'id, name', label: (r) => text(r.name) },
  asset_categories: { key: 'id', columns: 'id, name', label: (r) => text(r.name) },
  gateways: {
    key: 'id',
    columns: 'id, display_name, identifier',
    label: (r) => text(r.display_name) || text(r.identifier),
  },
  api_keys: {
    key: 'id',
    columns: 'id, label, key_prefix',
    label: (r) => text(r.label) || text(r.key_prefix),
  },
  companies: { key: 'id', columns: 'id, name', label: (r) => text(r.name) },
  profiles: { key: 'user_id', columns: 'user_id, full_name', label: (r) => text(r.full_name) },
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `table:id` → the record's name; `null` = looked up and not found (deleted). Tables without a name are absent. */
export function useSupportRecordLabels(sessionId: string, changes: SupportChange[] | undefined) {
  const wanted = new Map<string, Set<string>>();
  for (const c of changes ?? []) {
    const target = recordTarget(c);
    if (!target || !LOOKUPS[target.table] || !UUID.test(target.id)) continue;
    const ids = wanted.get(target.table) ?? new Set<string>();
    ids.add(target.id);
    wanted.set(target.table, ids);
  }
  return useQuery({
    queryKey: ['support', 'labels', sessionId, changes?.length ?? 0],
    enabled: wanted.size > 0,
    queryFn: async () => {
      const out = new Map<string, string | null>();
      await Promise.all(
        [...wanted].map(async ([table, ids]) => {
          const lookup = LOOKUPS[table]!;
          const list = [...ids];
          const found = new Map<string, string>();
          try {
            for (let i = 0; i < list.length; i += 100) {
              const got = await rows<Row[]>(
                supabase()
                  .from(table)
                  .select(lookup.columns)
                  .in(lookup.key, list.slice(i, i + 100)),
              );
              for (const r of got) found.set(String(r[lookup.key]), lookup.label(r));
            }
          } catch {
            return; // unreadable table: show the type without a name rather than fail the log
          }
          for (const id of list) out.set(`${table}:${id}`, found.get(id) ?? null);
        }),
      );
      return out;
    },
  });
}

/** The places a support role can be given in an account (Part C): warehouses, open projects, lone locations. */
export function useSupportRolePlaces(companyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['support', 'role-places', companyId],
    enabled: enabled && !!companyId,
    queryFn: () =>
      rows<SupportPlace[]>(supabase().rpc('support_role_places', { p_company: companyId })),
  });
}

/** Names for the places in a visit's role changes (sites and projects, read live; RLS applies). */
export function useSupportPlaceNames(sessionId: string, roles: SupportRoleChange[]) {
  const siteIds = [...new Set(roles.flatMap((r) => r.act_site_ids ?? []))];
  const projectIds = [...new Set(roles.flatMap((r) => r.act_project_ids ?? []))];
  return useQuery({
    queryKey: ['support', 'place-names', sessionId, siteIds.length + projectIds.length],
    enabled: siteIds.length + projectIds.length > 0,
    queryFn: async () => {
      const out = new Map<string, string>();
      const [sites, projects] = await Promise.all([
        siteIds.length
          ? rows<{ id: string; name: string }[]>(
              supabase().from('sites').select('id, name').in('id', siteIds),
            ).catch(() => [])
          : [],
        projectIds.length
          ? rows<{ id: string; name: string }[]>(
              supabase().from('projects').select('id, name').in('id', projectIds),
            ).catch(() => [])
          : [],
      ]);
      for (const r of [...sites, ...projects]) out.set(r.id, r.name);
      return out;
    },
  });
}
